import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import SourceBadge from "@/components/SourceBadge";
import LayerLine from "@/components/LayerLine";   // Batch 32 · B32-1 — the layer sentence (was the LayerBadge pill)
import { asArray } from "@/lib/asArray";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

const fmt = (v) => (v === null || v === undefined) ? "—" : fmtNumber(Number(Object.is(Number(v), -0) || Math.abs(Number(v)) < 0.000005 ? 0 : v), { decimals: 4 }); // Round 27 · X4: never "-0"; Batch 6 · E (AT-62): fixed 4 decimals

export default function Ratios() {
  const colW = useResizableColumns({ storageKey: "ratios", defaults: [160, 130, 160, 160, 240], mins: [100, 90, 100, 100, 130] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const [data, setData] = useState(null);
  const load = () => (current ? api.get(`/analytics/${current.id}/ratios`).then((r) => setData(r.data)).catch(() => {}) : Promise.resolve());
  useEffect(() => { load(); }, [current?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Batch 28 · Follow-up 1 · F2 (b): the remarks column is a row draft — one Save / Cancel bar under the ratios; Save sends only
  // the changed remarks (the existing per-ratio PUT), a failed one stays highlighted with its error. (Before: saved on blur,
  // errors silent.)
  const td = useTableDraft({
    rows: data ? asArray(data.ratios) : [], getId: (r) => r.name,
    saveRow: (name, patch) => api.put(`/analytics/${current.id}/ratios/remark`, { ratio_name: name, remark: patch.remark || "" }),
    reload: load,
  });

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;

  const groups = {};
  data.ratios.forEach((r) => {
    groups[r.group] = groups[r.group] || [];
    groups[r.group].push(r);
  });

  return (
    <div className="space-y-6" data-testid="ratios-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("ratios.title")} <SourceBadge source={data?.source} className="ms-2 align-middle" /></h1>
        <LayerLine screen="ratios" />
        <p className="text-sm text-muted-foreground">{t("ratios.subtitleSrc").replace("{source}", data?.source === "gl" ? t("analytics.sourceGl") : t("analytics.sourceTb"))}</p>
      </div>

      {Object.entries(groups).map(([group, ratios]) => (
        <Card key={group} data-testid={`ratio-group-${group}`}>
          <CardHeader><CardTitle className="text-base">{group}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ResizableTable cols={colW} wrap className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-4 py-2 text-start w-1/5">{t("ratios.ratio")}</ResizableTh>
                  <ResizableTh index={1} className="px-4 py-2 text-end w-24">{t("ratios.value")}</ResizableTh>
                  <ResizableTh index={2} className="px-4 py-2 text-start w-1/4">{t("ratios.formula")}</ResizableTh>
                  <ResizableTh index={3} className="px-4 py-2 text-start">{t("ratios.interpretation")}</ResizableTh>
                  <ResizableTh index={4} className="px-4 py-2 text-start w-64">{t("ratios.remarksComments")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {ratios.map((r) => (
                  <tr key={r.name} className={`border-b align-top ${td.rowClass(r.name)}`} data-changed={td.isChanged(r.name) ? "1" : "0"} data-failed={td.errorOf(r.name) ? "1" : "0"} data-testid={`ratio-row-${r.name.replace(/\s+/g, "-")}`}>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 num-cell text-lg font-semibold" data-testid={`ratio-value-${r.name}`}>{r.format === "pct" && r.value !== null && r.value !== undefined ? `${fmtNumber((Number(r.value) * 100))} %` : fmt(r.value)}</td>
                    <td className="px-4 py-2 text-xs font-mono text-slate-600 dark:text-slate-400">{r.formula}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{r.interpretation}</td>
                    <td className="px-4 py-2">
                      <Textarea
                        value={td.rowValue(r).remark || ""}
                        onChange={(e) => td.edit(r.name, "remark", e.target.value)}
                        rows={2}
                        placeholder={t("ratios.addRemark")}
                        className="text-xs min-h-[36px]"
                        data-testid={`ratio-remark-${r.name.replace(/\s+/g, "-")}`}
                      />
                      <RowSaveError draft={td} id={r.name} testid={`ratio-row-error-${r.name.replace(/\s+/g, "-")}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      ))}

      <TableSaveBar draft={td} lang={lang} testid="ratios-table-save" />

      <Card>
        <CardHeader><CardTitle className="text-base">{t("ratios.underlyingTotals")}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Object.entries(data.totals).map(([k, v]) => (
            <div key={k} className="rounded-md border p-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
              <div className="mt-1 num-cell font-medium">{fmtNumber(Number(v || 0))}</div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
